import React, { FunctionComponent } from 'react';

import { UserJourneyStep } from '@gojob/cms';
import Card, { CardProps } from '@gojob/ui-bits/Card';
import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';
import Arrow from '@gojob/ui-bits/Zigouigouis/ArrowLeft';
import Whirl from '@gojob/ui-bits/Zigouigouis/WhirlLeft';

import Buttons from '../../Section/UserJourney/Buttons';

export interface StepProps extends Omit<CardProps<'div'>, 'title'>, UserJourneyStep {
  index?: number;
}

const useStyles = (index = 0) => {
  const { spacing, colors, breakpoint } = useTheme();
  return {
    root: {
      position: 'relative',
      background: colors.white,
    },
    index: {
      position: 'relative',
      zIndex: 1,
      fontSize: spacing(24),
      lineHeight: 1,
      [breakpoint.up('md')]: {
        fontSize: spacing(15),
        lineHeight: spacing(22),
        minWidth: spacing(15),
      },
    },
    title: {
      marginTop: 0,
    },
    zigouigoui: {
      color: colors.secondary,
      position: 'absolute',
      width: spacing(15),
      top: spacing(-8),
      left: 0,
      transform: 'rotate(50deg) rotateY(180deg)',
    },
    zigouigouiRight: {
      color: colors.secondary,
      position: 'absolute',
      width: spacing(15),
      top: spacing(-8),
      right: '0',
      transform: 'rotate(-50deg)',
    },
    zIndex: {
      position: 'relative',
      zIndex: 1,
    },
  } as const;
};

export interface ZigouigouiProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref'> {
  index?: number;
  classNames?: string;
}

const Zigouigoui: FunctionComponent<ZigouigouiProps> = (props) => {
  const { index = 1, ...other } = props;
  switch (index) {
    case 1:
      return null;
    case 2:
    case 5:
      return <Arrow {...other} />;
    default:
      return <Whirl {...other} />;
  }
};

const Step: FunctionComponent<StepProps> = (props) => {
  const { index = 1, text, withStoreButtons, title: _title = {}, ...other } = props;
  const { title, level = 'h3' } = _title;
  const classes = useStyles(index);

  return (
    <Card padding={4} {...other} flexDirection="column" css={classes.root}>
      <Flex flexDirection={{ xs: 'column', md: 'row' }}>
        <Text variant="headline1" color="dodgerBlue" css={classes.index}>
          {index}
        </Text>
        <Flex flexDirection="column" css={classes.zIndex}>
          <Text variant="headline1" as={level} gutter={4} css={classes.title}>
            {title}
          </Text>
          <Text variant="paragraph" gutter={withStoreButtons ? 4 : 0}>
            {text}
          </Text>
        </Flex>
      </Flex>
      {withStoreButtons && <Buttons />}
      <Zigouigoui index={index} css={index % 2 ? classes.zigouigoui : classes.zigouigouiRight} />
    </Card>
  );
};

export default Step;
